// Management Company Detail Page (BUILD_SPEC §5.1).
// Header with the 4 KPIs + accordion list of regionals (Unassigned
// pinned to the bottom). Each regional expands to its property list,
// with Apollo customers green-tinted.

import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import DirectoryTopNav from '../components/DirectoryTopNav';
import PropertyRow from '../components/PropertyRow';

export default function DirectoryCompanyDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openIds, setOpenIds] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);

  async function onExport() {
    setExporting(true);
    try {
      await api.downloadDirectoryCsv({ view: 'company', slug });
    } catch (err) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.getDirectoryCompany(slug)
      .then(d => { if (!cancelled) setCompany(d.company); })
      .catch(err => { if (!cancelled) setError(err?.responseBody?.error || err.message || 'Failed to load company'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const regionals = useMemo(() => {
    if (!company?.regionals) return [];
    // Server already sorts with Unassigned last; trust it but defend.
    return company.regionals.slice().sort((a, b) => {
      if (a.is_unassigned && !b.is_unassigned) return 1;
      if (b.is_unassigned && !a.is_unassigned) return -1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [company]);

  function toggle(id) {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function expandAll() {
    setOpenIds(new Set(regionals.map(r => r.id)));
  }
  function collapseAll() {
    setOpenIds(new Set());
  }

  if (loading) {
    return (
      <div className="directory-page">
        <DirectoryTopNav />
        <div className="loading">Loading company...</div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="directory-page">
        <DirectoryTopNav />
        <div className="card" style={{ padding: 24, marginTop: 16 }}>
          <h2>Management company not found</h2>
          <p style={{ color: '#475569' }}>{error || 'Try the company browser.'}</p>
          <button className="btn btn-primary" onClick={() => navigate('/sales/directory/companies')}>Back to companies</button>
        </div>
      </div>
    );
  }

  const opps = (company.property_count || 0) - (company.apollo_customer_count || 0);

  return (
    <div className="directory-page">
      <DirectoryTopNav />

      <button
        className="directory-back"
        type="button"
        onClick={() => navigate('/sales/directory/companies')}
      >
        &larr; Back to companies
      </button>

      {/* Company header — large KPI strip */}
      <section className="card directory-property-card">
        <div className="directory-property-card__head">
          <h1 className="directory-property-card__name">{company.name}</h1>
          {isSuperAdmin ? (
            <button
              type="button"
              className="btn btn-sm directory-export-btn"
              onClick={onExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          ) : null}
        </div>
        <div className="directory-kpi-row">
          <Kpi value={company.property_count} label="Total Props" />
          <Kpi value={company.apollo_customer_count || 0} label="Apollo" accent="green" />
          <Kpi value={opps} label="Opportunities" accent={opps > 0 ? 'amber' : null} />
          <Kpi value={`${company.coverage_pct || 0}%`} label="Coverage" />
          <Kpi value={company.regional_count} label="Regionals" />
        </div>
      </section>

      <div className="directory-section">
        <div className="directory-section__header">
          <h2 className="directory-section__title">Regionals</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="directory-link" onClick={expandAll}>Expand all</button>
            <span style={{ color: '#cbd5e1' }}>|</span>
            <button type="button" className="directory-link" onClick={collapseAll}>Collapse all</button>
          </div>
        </div>

        <div className="directory-accordion">
          {regionals.map(r => (
            <RegionalGroup
              key={r.id}
              regional={r}
              open={openIds.has(r.id)}
              onToggle={() => toggle(r.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function RegionalGroup({ regional, open, onToggle }) {
  const apollo = regional.apollo_customer_count || 0;
  const total = regional.property_count || 0;
  const cov = regional.coverage_pct || 0;
  const contact = [regional.email, regional.phone].filter(Boolean).join(' · ');
  return (
    <div className={'directory-accordion__group' + (regional.is_unassigned ? ' directory-accordion__group--unassigned' : '')}>
      <button
        type="button"
        className="directory-accordion__toggle"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="directory-accordion__chev" aria-hidden="true">{open ? '\u25BC' : '\u25B6'}</span>
        <span className="directory-accordion__name">
          {regional.is_unassigned ? <em>Unassigned</em> : regional.name}
        </span>
        <span className="directory-accordion__stats">
          <strong>{total}</strong> props &middot; <strong>{apollo}</strong> Apollo &middot; <strong>{cov}%</strong>
        </span>
      </button>

      {open ? (
        <div className="directory-accordion__body">
          {contact ? (
            <p className="directory-accordion__contact">
              {regional.email ? <a href={`mailto:${regional.email}`}>{regional.email}</a> : null}
              {regional.email && regional.phone ? ' · ' : ''}
              {regional.phone ? <a href={`tel:${regional.phone.replace(/[^0-9+]/g, '')}`}>{regional.phone}</a> : null}
            </p>
          ) : null}
          <div className="directory-regional-book" style={{ maxHeight: 'none' }}>
            {(regional.properties || []).map(p => (
              <PropertyRow key={p.id} property={p} />
            ))}
            {!regional.properties || regional.properties.length === 0 ? (
              <p style={{ color: '#64748b', padding: 12 }}>No properties.</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Kpi({ value, label, accent }) {
  const cls = 'directory-kpi' + (accent ? ` directory-kpi--${accent}` : '');
  return (
    <div className={cls}>
      <div className="directory-kpi__value">{value}</div>
      <div className="directory-kpi__label">{label}</div>
    </div>
  );
}
